/**
 * Main Application Component
 *
 * Root component of the React application.
 * Sets up routing and layout structure.
 * Mobile-first responsive design for couples communication analysis platform.
 */

import { Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useAnalytics } from "./lib/analytics";
import { ErrorPage } from "./pages/ErrorPage";
import { HomePage } from "./pages/HomePage";
import { GetStartedPage } from "./pages/GetStartedPage";
import { GrowthPage } from "./pages/GrowthPage";
import { CheckInPage } from "./pages/CheckInPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { LegalPage } from "./pages/LegalPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ReflectionsPage } from "./pages/ReflectionsPage";
import { ReflectionDetailPage } from "./pages/ReflectionDetailPage";
import { TrackerPage } from "./pages/TrackerPage";
import { TrackerEntryPage } from "./pages/TrackerEntryPage";
import { AccountPage } from "./pages/AccountPage";
import { PartnersPage } from "./pages/PartnersPage";
import { PartnerDetailPage } from "./pages/PartnerDetailPage";
import { SupportPage } from "./pages/SupportPage";
import { SharedReflectionPage } from "./pages/SharedReflectionPage";
import { InvitationsPage } from "./pages/InvitationsPage";
import { InvitationTokenPage } from "./pages/InvitationTokenPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { LogoutPage } from "./pages/LogoutPage";
import { LoginPage } from "./pages/LoginPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SubscriptionRequiredRoute } from "./components/SubscriptionRequiredRoute";

// Forces SharedReflectionPage to fully remount when the token changes,
// preventing stale state when navigating between different share links.
function SharedReflectionPageWithKey() {
  const location = useLocation();
  return <SharedReflectionPage key={location.pathname} />;
}

function App() {
  useAnalytics();
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/get-started" element={<GetStartedPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route
          path="/dashboard"
          element={
            <SubscriptionRequiredRoute>
              <DashboardPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/reflections"
          element={
            <SubscriptionRequiredRoute>
              <ReflectionsPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/reflections/:reflectionId"
          element={
            <SubscriptionRequiredRoute>
              <ReflectionDetailPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/tracker"
          element={
            <SubscriptionRequiredRoute>
              <TrackerPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/tracker/entries/:date"
          element={
            <SubscriptionRequiredRoute>
              <TrackerEntryPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <SubscriptionRequiredRoute>
              <GrowthPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/growth/check-ins/:id"
          element={
            <SubscriptionRequiredRoute>
              <CheckInPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/partners"
          element={
            <SubscriptionRequiredRoute>
              <PartnersPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/partners/:partnerId"
          element={
            <SubscriptionRequiredRoute>
              <PartnerDetailPage />
            </SubscriptionRequiredRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invitations"
          element={
            <ProtectedRoute>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/invitations/:token" element={<InvitationTokenPage />} />
        <Route path="/share/:token" element={<SharedReflectionPageWithKey />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/error" element={<ErrorPage error={{ status: 500 }} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
